import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useCardano } from "@cardano-foundation/cardano-connect-with-wallet";
import { toast } from "react-toastify";

import { Icon } from "@ui/icons/icon";
import { Loader } from "@ui/loader/loader";
import { useTheme } from "@/components/theme-context/theme-context";
import { useBookmarks } from "@/providers/bookmarks-provider/hooks/use-bookmarks";
import { useBookmarkHandlers } from "@/providers/bookmarks-provider/hooks/use-bookmark-handlers";

import { ICON_COLLECTION } from "@ui/icons/icon-list";
import { ROUTES } from "@/constants/routes/routes";
import { fetchFundDetails } from "@/api/fund-details";
import { depositToFund } from "@/api/deposit";
import { DepositModal } from "../deposit-modal/deposit-modal";
import { findAddressWithBalance, hexToBytes } from "@/helpers/utxo-selection";
import { CARDANO_NETWORK } from "@constants/config";

export const FundDetailsNav = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDepositModalOpen, setIsDepositModalOpen] = useState(false);

  const { bookmarks } = useBookmarks();
  const { handleBookmark } = useBookmarkHandlers();
  const { enabledWallet } = useCardano({ limitNetwork: CARDANO_NETWORK });

  const { fund_id } = useParams();

  const {
    data: fund,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["fund-details", fund_id],
    queryFn: fetchFundDetails,
    enabled: !!fund_id,
  });

  // todo: error message?
  if (!fund) {
    return (
      <div className="w-full flex items-center justify-between rounded-lg bg-white dark:bg-gray-900 p-6">
        <Loader size="43" />
      </div>
    );
  }

  const isBookmarked = bookmarks.find((id) => id === fund_id);

  // Use fund_name from backend if available, otherwise decode from hex
  const fundName = fund.fund_name || (() => {
    try {
      const nameHex = fund.name.substring(0, 16);
      let str = '';
      for (let i = 0; i < nameHex.length; i += 2) {
        str += String.fromCharCode(parseInt(nameHex.substring(i, i + 2), 16));
      }
      return str;
    } catch (e) {
      return fund.name;
    }
  })();

  // Handle deposit
  const handleDeposit = async (multiple: number) => {
    try {
      if (!enabledWallet) {
        throw new Error("Please connect your wallet first");
      }

      if (!("cardano" in window)) {
        throw new Error("Cardano wallet not found");
      }

      const cardano = (window as any).cardano;
      const walletAPI = await cardano[enabledWallet].enable();

      // Get UTXOs to find an address with enough funds
      const utxosHex = await walletAPI.getUtxos();

      // The multiple is the deposit amount (it represents how many times the fund_factor)
      // We need at least some ADA for the transaction (estimating 5 ADA should be enough)
      const requiredLovelace = 5_000_000; // 5 ADA minimum for now
      const userAddress = await findAddressWithBalance(utxosHex, requiredLovelace);

      // Call backend to build deposit transaction
      const unsignedTxCbor = await depositToFund(
        fund.name, // The fund_id is the full hex name
        userAddress,
        multiple
      );

      console.log("=== DEPOSIT TRANSACTION ===");
      console.log("Unsigned TX CBOR:", unsignedTxCbor);

      // Import CSL
      const CSL = await import("@emurgo/cardano-serialization-lib-asmjs");

      // Parse the transaction
      let backendTx;
      try {
        backendTx = CSL.Transaction.from_bytes(hexToBytes(unsignedTxCbor));
      } catch (e) {
        console.log("Trying from_hex...");
        backendTx = CSL.Transaction.from_hex(unsignedTxCbor);
      }

      console.log("Parsed transaction:", backendTx);

      // Get user signature from wallet
      console.log("Signing transaction...");
      const witnessSetCbor = await walletAPI.signTx(unsignedTxCbor, true);

      // Parse the wallet's witness set
      const walletWitnessSet = CSL.TransactionWitnessSet.from_bytes(
        hexToBytes(witnessSetCbor)
      );

      // Merge witness sets (backend redeemers + wallet signatures)
      const backendWitnessSet = backendTx.witness_set();
      const mergedWitnessSet = CSL.TransactionWitnessSet.new();

      // Add redeemers from backend
      const redeemers = backendWitnessSet.redeemers();
      if (redeemers) {
        mergedWitnessSet.set_redeemers(redeemers);
      }

      // Add plutus scripts from backend
      const plutusScripts = backendWitnessSet.plutus_scripts();
      if (plutusScripts) {
        mergedWitnessSet.set_plutus_scripts(plutusScripts);
      }

      // Add plutus data from backend
      const plutusData = backendWitnessSet.plutus_data();
      if (plutusData) {
        mergedWitnessSet.set_plutus_data(plutusData);
      }

      // Add vkeys from wallet
      const walletVkeys = walletWitnessSet.vkeys();
      if (walletVkeys) {
        mergedWitnessSet.set_vkeys(walletVkeys);
      } else {
        throw new Error("Wallet did not return vkey witnesses");
      }

      // Build final signed transaction
      const signedTx = CSL.Transaction.new(
        backendTx.body(),
        mergedWitnessSet,
        backendTx.auxiliary_data()
      );

      const signedTxCbor = signedTx.to_hex();

      // Submit transaction
      console.log("Submitting transaction...");
      const txHash = await walletAPI.submitTx(signedTxCbor);

      console.log("Transaction submitted successfully!");
      console.log("Transaction hash:", txHash);

      toast.success(
        `Deposit successful! TX: ${txHash.substring(0, 16)}...`
      );
    } catch (error) {
      console.error("Deposit error:", error);
      toast.error(
        `Failed to deposit: ${error instanceof Error ? error.message : "Unknown error"}`
      );
      throw error;
    }
  };

  return (
    <>
      <DepositModal
        isOpen={isDepositModalOpen}
        onClose={() => setIsDepositModalOpen(false)}
        onDeposit={handleDeposit}
        fundId={fund_id ?? ""}
        fund={fund}
      />

      {isLoading || isFetching ? (
        <div className="w-full flex items-center justify-between rounded-lg bg-white dark:bg-gray-900 p-6">
          <Loader size="43" />
        </div>
      ) : (
        <div className="w-full flex items-center justify-between rounded-lg bg-white dark:bg-gray-900 p-6">
          <div className="flex items-center gap-12px">
            <Link
              to={ROUTES.home.path}
              className="flex items-center justify-center cursor-pointer"
            >
              <Icon
                icon={ICON_COLLECTION.arrowLeft}
                iconColor={theme === "dark" ? "#fff" : "#1F2937"}
              />
            </Link>
            <div className="relative" style={{ width: "40px", height: "40px" }}>
              <div className="absolute bottom-0 left-0 z-30 w-6 h-6 rounded-full bg-violet-700 border border-white flex items-center justify-center font-bold font-sf-pro text-white text-xs">
                {fund?.tokens.length ?? 0}
              </div>
            </div>
            <h3 className="text-gray-800 dark:text-white text-2xl font-semibold font-sf-pro">
              {fundName}
            </h3>
            <div className="flex items-center justify-center px-2 rounded-full text-violet-600 bg-violet-200 dark:bg-gray-600 dark:text-white">
              {t("funds-list-table.table.featured")}
            </div>
          </div>
          <div className="hidden md:flex items-center gap-24px">
            <button
              onClick={() => handleBookmark(fund_id ?? "")}
              className="flex items-center justify-center rounded-full bg-violet-50 dark:bg-gray-800 p-6px cursor-pointer hover:dark:bg-gray-600 transition-all duration-300"
            >
              <Icon
                icon={
                  !!isBookmarked
                    ? ICON_COLLECTION.bookmarkFilled
                    : ICON_COLLECTION.bookmark
                }
              />
            </button>
            <button
              onClick={() => setIsDepositModalOpen(true)}
              className="py-12px px-32px rounded-full bg-violet-200 dark:bg-gray-800 hover:bg-violet-300 hover:dark:bg-gray-700 transition-all duration-300 text-l font-sf-pro font-semibold text-violet-600 dark:text-white"
            >
              {t("fund-details.navBtns.desk")}
            </button>
          </div>
          <div className="block md:hidden relative">
            <div
              className="p-12px flex items-center justify-center rounded-lg border border-violet-400 dark:border-gray-700"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <Icon
                icon={ICON_COLLECTION.moreHorizontal}
                iconSize="24px"
                iconColor="#9CA3AF"
              />
            </div>
            {isDropdownOpen ? (
              <div
                className="w-64 p-12px flex flex-col gap-12px absolute bottom-0 left-0 translate-y-full -translate-x-2/3 bg-white dark:bg-gray-950 rounded-lg"
                style={{
                  boxShadow:
                    theme === "dark"
                      ? ""
                      : "0px 10px 20px 6px rgba(75, 85, 99, 0.10)",
                }}
              >
                <button
                  onClick={() => {
                    setIsDepositModalOpen(true);
                    setIsDropdownOpen(false);
                  }}
                  className="w-full flex items-center justify-center gap-8px py-8px px-24px rounded-full dark:bg-gray-800 bg-violet-200 font-sf-pro text-s font-semibold text-violet-600 dark:text-white whitespace-normal"
                >
                  {t("fund-details.navBtns.mobileAdd")}
                  <Icon
                    icon={ICON_COLLECTION.plus}
                    iconSize="16px"
                    iconColor={theme === "dark" ? "#fff" : "#7C3AED"}
                  />
                </button>
                <button
                  onClick={() => {
                    handleBookmark(fund_id ?? "");
                    setIsDropdownOpen(false);
                  }}
                  className="w-full flex items-center justify-center gap-8px py-8px px-24px rounded-full dark:bg-gray-800 bg-violet-200 font-sf-pro text-s font-semibold text-violet-600 dark:text-white whitespace-normal"
                >
                  {t("fund-details.navBtns.mobileBook")}
                  <Icon
                    icon={
                      !!isBookmarked
                        ? ICON_COLLECTION.bookmarkFilled
                        : ICON_COLLECTION.bookmark
                    }
                    iconSize="16px"
                    iconColor={theme === "dark" ? "#fff" : "#7C3AED"}
                  />
                </button>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </>
  );
};
